'use client';

import { useMicrocopy } from '@lib/cms/microcopy/client';
import {
  hasCountdownTimeRemaining,
  useCountdown,
} from '@lib/hooks/useCountdown';
import c from 'classnames';
import { formatCountdown } from 'ui-components';
import { interpolateStringWithData } from 'utils';
import { Sale } from './types';

type ProductCardSaleCountdownProps = {
  sale: Sale;
  isActive?: boolean;
  /**
   * True when a -X% discount badge renders directly before the pill. The
   * Figma sale badge is one container — label · 1px white divider ·
   * countdown — and the pill reproduces it by fusing onto the badge and
   * drawing the divider itself, so the divider and countdown appear and
   * disappear together with the pill (which returns null until the countdown
   * is computed, and again once no time remains).
   */
  attachedToDiscount?: boolean;
};

/**
 * Renders the sale countdown as an inline pill in the product card price row
 * (red for an active sale, green "Sale starts …" for an upcoming flash deal),
 * matching the price component in Figma. Returns null until the countdown is
 * ready; the pill also suppresses hydration warnings since the time value
 * differs between the server render and the first client render.
 */
export const ProductCardSaleCountdown = ({
  sale,
  isActive,
  attachedToDiscount,
}: ProductCardSaleCountdownProps) => {
  // The pill owns its sale-countdown-* keys, deliberately separate from the
  // legacy product-card.sale-starts / countdown-with-days entries, whose CMS
  // values carry the old wording and three-unit format (CPS-7269). The
  // unit-pair key names double as the format contract for translators: always
  // two units.
  const microcopy = useMicrocopy('product-card', {
    'product-card.sale-countdown-starts': 'Sale starts {{time}}',
    'product-card.sale-countdown-ends-aria-label': 'Sale ends in {{time}}',
    'product-card.sale-countdown-days-hours': '{{days}}d {{hours}}h',
    'product-card.sale-countdown-hours-minutes': '{{hours}}h {{minutes}}m',
    'product-card.sale-countdown-minutes-seconds': '{{minutes}}m {{seconds}}s',
  });

  const countdownDate = isActive ? sale.dates.ends : sale.dates.starts;
  const countdownDuration = useCountdown(countdownDate);

  if (!countdownDuration || !countdownDate) {
    return null;
  }

  if (!hasCountdownTimeRemaining(countdownDuration)) {
    return null;
  }

  const countdownFormatted = formatCountdown(countdownDate, countdownDuration, {
    withDays: microcopy['product-card.sale-countdown-days-hours'],
    hoursOnly: microcopy['product-card.sale-countdown-hours-minutes'],
    minutesOnly: microcopy['product-card.sale-countdown-minutes-seconds'],
  });

  return (
    <span
      // Figma sale badge: one 4px-radius container with the -X% label, a 1px
      // full-height white divider, and the countdown (#E12727), all 4px
      // apart. Reproduced without touching the shared -X% badge: when
      // attached, the pill overlaps it by 4px (-ml-3 = the row's 8px gap +
      // 4px) so its square left edge paints over the badge's rounded right
      // corners (same background, later sibling), then rebuilds the covered
      // padding (pl-1) and draws the divider itself. Overlapping rather than
      // having the parent switch the badge to rounded-l is what makes this
      // self-healing: the badge always keeps its own rounded, so it reads
      // correctly on the renders where this pill returns null — before the
      // duration is computed, and at the sale boundary via the
      // hasCountdownTimeRemaining zero-guard — which the parent can't
      // predict.
      className={c(
        'inline-flex items-center gap-1 whitespace-nowrap text-xs leading-5',
        attachedToDiscount
          ? '-ml-3 rounded-l-none rounded-r px-1'
          : 'rounded px-1',
        {
          // Figma specifies #E12727 (red-500) for the countdown, but that is
          // ~4.0:1 on the red-200 chip — below WCAG AA's 4.5:1 for 12px text.
          // red-600 (#D3171E, the label's color) is 4.6:1 and passes; the
          // spec needs a design revisit for the two-tone hierarchy.
          'bg-danger-surface text-danger': isActive,
          'bg-positive-surface text-green-700 dark:text-positive': !isActive,
        }
      )}
      data-test="product-card-flash-deal-info"
      data-sale-type={sale.type}
      suppressHydrationWarning
    >
      {attachedToDiscount && (
        <span aria-hidden className="w-px self-stretch bg-surface" />
      )}
      {isActive ? (
        <>
          {/* The visible pill is a bare duration, so screen readers get the
              full "Sale ends in …" phrasing instead */}
          <span className="sr-only" suppressHydrationWarning>
            {interpolateStringWithData(
              microcopy['product-card.sale-countdown-ends-aria-label'],
              { time: countdownFormatted }
            )}
          </span>
          <span
            aria-hidden
            data-test="active-flash-deal-stage"
            // React only suppresses text-mismatch warnings on the element
            // whose direct text child differs, so the flag must sit here too
            suppressHydrationWarning
          >
            {countdownFormatted}
          </span>
        </>
      ) : (
        <span data-test="upcoming-flash-deal-stage" suppressHydrationWarning>
          {interpolateStringWithData(
            microcopy['product-card.sale-countdown-starts'],
            { time: countdownFormatted }
          )}
        </span>
      )}
    </span>
  );
};
